import { useState, useEffect, useCallback } from "react";
import { ScrollText } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ENTITIES = ["all", "patient", "attendance", "payment", "deposit", "expense", "settlement", "user", "home_visit", "settings"];

export default function AuditLogs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = entity !== "all" ? { entity } : {};
      const { data } = await api.get("/audit-logs", { params });
      setItems(data.items);
    } finally { setLoading(false); }
  }, [entity]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Audit Logs" subtitle="Every financial & operational change is traceable" testid="audit-page">
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger className="w-44" data-testid="audit-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{ENTITIES.map((e) => <SelectItem key={e} value={e} className="capitalize">{e.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      {loading ? <Loading /> : items.length === 0 ? <EmptyState icon={ScrollText} title="No audit records" /> : (
        <Card className="divide-y">
          {items.map((a) => (
            <div key={a.id} className="flex items-start gap-3 px-4 py-3" data-testid={`audit-${a.id}`}>
              <Badge variant="outline" className="capitalize shrink-0 mt-0.5">{a.action?.replace(/_/g, " ")}</Badge>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-700 font-medium">{a.details || `${a.user_name} (${a.user_role}) performed ${a.action?.replace(/_/g, " ")} on ${a.entity}`}</p>
                <p className="text-xs text-slate-400">By {a.user_name} ({a.user_role}) · {a.entity} {a.entity_id ? `#${String(a.entity_id).slice(0, 8)}` : ""}</p>
                {(!a.details && (a.new_value || a.old_value)) && <p className="text-xs text-slate-400 truncate">{JSON.stringify(a.new_value || a.old_value)}</p>}
              </div>
              <span className="text-xs text-slate-400 shrink-0">{fmtDateTime(a.timestamp)}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
